
import React, { useState, useEffect } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Menu, X, ChevronRight } from 'lucide-react';
import { Button } from "@/components/ui/button"
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('hero');

  // Track scroll position to update navbar appearance and active link
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
      
      // Determine active section based on scroll position
      const sections = ['hero', 'services', 'how-we-work', 'who-we-help', 'legal-services', 'contact'];
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            setActiveLink(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const navLinks = [
    { title: "Solutions", to: "services" },
    { title: "Process", to: "how-we-work" },
    { title: "Engineering", to: "who-we-help" },
    { title: "Legal", to: "legal-services" },
  ];

  // Custom scroll function to target emoji
  const handleScrollTo = (to) => {
    const section = document.getElementById(to);
    if (section) {
      const navbarHeight = scrolled ? 64 : 80; // Adjust based on navbar height state
      
      // Find the emoji element within the section
      const emojiElement = section.querySelector('.text-xl, .text-2xl');
      
      if (emojiElement) {
        // Calculate position of the emoji element
        const emojiPosition = emojiElement.getBoundingClientRect().top + window.pageYOffset;
        
        // Add a small offset for better positioning
        const scrollPosition = emojiPosition - navbarHeight - 20;
        
        window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      } else {
        // Fallback to the section top if emoji not found
        const targetPosition = section.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: targetPosition - navbarHeight,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? "bg-[#001233]/95 backdrop-blur-md shadow-lg h-14 md:h-16" 
          : "bg-[#001233]/70 backdrop-blur-sm h-16 md:h-20"
      }`}
    >
      <div className="container mx-auto px-3 sm:px-4 h-full">
        <div className="flex items-center justify-between h-full">
          {/* Replace standard ScrollLink with custom scroll function */}
          <div
            onClick={() => handleScrollTo('hero')}
            className="flex items-center cursor-pointer group"
          >
            <motion.img 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              src="/lovable-uploads/c7b39752-6d09-4df6-b424-5dee5512788f.png"
              alt="NextGen Startup Logo"
              className="h-6 md:h-7 w-auto object-contain transition-all duration-300 group-hover:brightness-125"
            />
          </div>
          
          <div className="hidden md:flex items-center justify-center space-x-8 lg:space-x-10">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.to}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.2 }}
              >
                {/* Replace standard ScrollLink with custom scroll function */}
                <div
                  onClick={() => handleScrollTo(link.to)}
                  className={`text-white hover:text-[#33C3F0] relative font-inter text-[14px] lg:text-[15px] font-medium cursor-pointer transition-colors group ${
                    activeLink === link.to ? "text-[#33C3F0]" : ""
                  }`}
                >
                  {link.title}
                  <span className={`absolute left-0 bottom-[-4px] h-[2px] bg-[#33C3F0] transition-all duration-300 ${
                    activeLink === link.to ? "w-full" : "w-0 group-hover:w-full"
                  }`}></span>
                </div>
              </motion.div>
            ))}
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
            >
              {/* Replace standard ScrollLink with custom scroll function */}
              <div
                onClick={() => handleScrollTo('contact')}
                className="cursor-pointer"
              >
                <Button 
                  variant="coral" 
                  size="sm" 
                  className="font-bold relative overflow-hidden group"
                >
                  <span className="relative z-10">Let's Talk</span>
                  <span className="absolute inset-0 bg-gradient-to-r from-[#FF6B6B] to-[#FF8C8C] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  <motion.span 
                    className="absolute right-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1"
                    animate={{ x: [0, 3, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5, repeatDelay: 0.5 }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </motion.span>
                </Button>
              </div>
            </motion.div>
          </div>

          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild className="md:hidden">
              <Button variant="ghost" className="relative h-9 w-9 rounded-full p-0 text-white hover:bg-white/10">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px] bg-gradient-to-b from-[#001233] to-[#00123F] border border-white/10">
              <DialogHeader className="flex flex-row items-center justify-between">
                <div>
                  <DialogTitle className="text-white font-medium">Menu</DialogTitle>
                  <DialogDescription className="text-gray-300">
                    Navigate through our solutions
                  </DialogDescription>
                </div>
                <Button 
                  variant="ghost" 
                  className="h-8 w-8 p-0 rounded-full hover:bg-white/10"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="h-4 w-4 text-white" />
                </Button>
              </DialogHeader>
              
              <div className="grid gap-1.5 py-4">
                <AnimatePresence>
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.to}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="overflow-hidden"
                    >
                      {/* Replace standard ScrollLink with custom scroll function */}
                      <div
                        onClick={() => {
                          handleScrollTo(link.to);
                          setIsOpen(false);
                        }}
                        className={`text-white hover:text-[#33C3F0] transition-all font-medium cursor-pointer p-3 block rounded-md hover:bg-white/5 flex items-center justify-between ${
                          activeLink === link.to ? "bg-white/10 text-[#33C3F0]" : ""
                        }`}
                      >
                        <div className="flex items-center">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                            activeLink === link.to ? "bg-[#33C3F0]" : "bg-white/50"
                          } mr-2.5`}></span>
                          {link.title}
                        </div>
                        <motion.div
                          animate={{ x: activeLink === link.to ? [0, 3, 0] : 0 }}
                          transition={{ repeat: activeLink === link.to ? Infinity : 0, duration: 1.5, repeatDelay: 0.5 }}
                        >
                          <ChevronRight className={`h-4 w-4 ${activeLink === link.to ? "opacity-100" : "opacity-50"}`} />
                        </motion.div>
                      </div>
                    </motion.div>
                  ))}
                  
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="px-1 mt-2"
                  >
                    {/* Replace standard ScrollLink with custom scroll function */}
                    <div
                      onClick={() => {
                        handleScrollTo('contact');
                        setIsOpen(false);
                      }}
                      className="cursor-pointer"
                    >
                      <Button 
                        variant="coral" 
                        className="w-full mt-3 group relative overflow-hidden font-bold"
                      >
                        <span className="relative z-10">Let's Talk</span>
                        <span className="absolute inset-0 bg-gradient-to-r from-[#FF6B6B] to-[#FF8C8C] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                        <motion.span 
                          className="absolute right-3 opacity-70 group-hover:opacity-100"
                          animate={{ x: [0, 3, 0] }}
                          transition={{ repeat: Infinity, duration: 1.5, repeatDelay: 0.5 }}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </motion.span>
                      </Button>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;
